import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import API from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Crown, PaperPlaneTilt, CurrencyGbp, Truck, House, Plus, ClipboardText, ArrowSquareOut, SealCheck } from "@phosphor-icons/react";
import QuoteComparisonDialog from "../QuoteComparisonDialog";
import ReverseCalcDialog from "../ReverseCalcDialog";
import QuickQuoteDialog, { EMPTY_QUICK_QUOTE } from "./QuickQuoteDialog";
import InviteSuppliersDialog from "./InviteSuppliersDialog";
import SupplierQuoteCard, { quoteTotals } from "./SupplierQuoteCard";
import { AddQuoteMenu } from "./AddQuoteMenu";
import QuoteQuickActions from "./QuoteQuickActions";
import { fmtMoney } from "./PortalQuoteActions";
import ClientApprovalLinkButton from "./ClientApprovalLinkButton";

const JR_LABEL = { submitted_to_client: "With client for approval", client_approved: "Approved by client", quotes_received: "Quotes received", converted: "Converted", declined: "Declined" };

/* Originating request's quote (jobs converted from a request) — kept from the old Quotes & Variations panel. */
function OriginStrip({ jr, onChanged }) {
  if (!jr) return null;
  const sel = (jr.quotes || []).find(x => x.id === jr.selected_quote_id) || null;
  const built = sel?.in_house_quote_id ? sel : (jr.quotes || []).filter(x => x.in_house_quote_id).slice(-1)[0] || null;
  const total = sel ? quoteTotals(sel).total : null;
  return (
    <div className="mb-3">
      <Link to={`/job-requests/${jr.id}`} className="flex items-center gap-3 rounded-md border border-border bg-muted/30 px-3 py-2 hover:border-primary/40" data-testid="job-origin-quote">
        <ClipboardText size={18} className="text-muted-foreground shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold truncate">Originating quote · {jr.reference}</p>
          <p className="text-[11px] text-muted-foreground truncate">
            {total != null ? `${fmtMoney(total)} ex VAT · ` : ""}{JR_LABEL[jr.status] || jr.status}
            {jr.client_response?.via === "client_portal" && ` · ${jr.client_response.decision === "decline" ? "declined" : "approved"} by ${jr.client_response.name} via portal`}
            {jr.client_po_number && ` · PO ${jr.client_po_number}`}
          </p>
        </div>
        <ArrowSquareOut size={14} className="text-muted-foreground" />
      </Link>
      {built && <div className="mt-1 pl-1 flex items-center gap-2 flex-wrap"><span className="text-[11px] text-muted-foreground font-mono">{built.quote_reference}</span><QuoteQuickActions quoteId={built.in_house_quote_id} reference={built.quote_reference} onChanged={onChanged} testId="job-origin-quote" /></div>}
    </div>
  );
}

/* Quotes on a job / sub-job — the Job Request quotes tab, one-for-one, plus an explicit Approve per quote. */
export default function EntityQuotesPanel({ job, onChanged, testPrefix = "job" }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const base = `/jobs/${job.id}`;
  const builderLink = `/quotes/new?job_id=${job.id}`;
  const quotes = useMemo(() => job.quotes || [], [job.quotes]);
  const [builders, setBuilders] = useState({});
  const [jr, setJr] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [markupTemplates, setMarkupTemplates] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [reverseCalc, setReverseCalc] = useState(null);

  const loadBuilders = useCallback(() => {
    API.get(`/quotes?job_id=${job.id}&limit=50`).then(({ data }) => setBuilders(Object.fromEntries((data.quotes || []).map(q => [q.id, q])))).catch(() => {});
  }, [job.id]);
  useEffect(() => { loadBuilders(); }, [loadBuilders, job.updated_at]);
  useEffect(() => { if (job.source_request_id) API.get(`/job-requests/${job.source_request_id}`).then(({ data }) => setJr(data)).catch(() => setJr(null)); }, [job.source_request_id]);
  useEffect(() => {
    API.get("/markup-templates").then(({ data }) => setMarkupTemplates(data.templates || [])).catch(() => {});
    API.get("/suppliers?limit=500").then(({ data }) => setSuppliers(Array.isArray(data) ? data : (data.vendors || data.suppliers || []))).catch(() => {});
  }, []);
  useEffect(() => {
    if (searchParams.get("quote") === "quick") {
      setShowAdd(true);
      const next = new URLSearchParams(searchParams); next.delete("quote"); setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const refresh = () => { onChanged?.(); loadBuilders(); };
  const run = async (fn, ok, fail) => { try { await fn(); toast.success(ok); refresh(); } catch (e) { toast.error(e.response?.data?.detail || fail); throw e; } };
  const addQuote = (form) => run(() => API.post(`${base}/quotes`, form), "Quote added", "Failed to add quote").then(() => setShowAdd(false));
  const saveQuote = (form) => run(() => API.put(`${base}/quotes/${editing.id}`, form), "Quote updated", "Failed to update quote").then(() => setEditing(null));
  const deleteQuote = (qid) => run(() => API.delete(`${base}/quotes/${qid}`), "Quote removed", "Failed to delete quote").catch(() => {});
  const selectQuote = (qid) => run(() => API.put(`${base}/select-quote/${qid}`), "Quote selected", "Failed to select quote").catch(() => {});
  const approveQuote = (qid, approved) => run(() => API.put(`${base}/quotes/${qid}/approve`, { approved }), approved ? "Quote approved" : "Approval withdrawn", "Failed to update approval").catch(() => {});
  const raiseWo = async (q) => {
    try {
      const { data } = await API.post(`${base}/quotes/${q.id}/raise-wo`);
      toast.success(`${data.revised ? "Works order revised" : "Works order raised"}: ${data.wo_number}`);
      refresh();
      navigate(`/work-orders?open=${data.works_order_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to raise works order"); }
  };
  const invite = (emails) => run(async () => { const { data } = await API.post(`${base}/request-vendor-quotes`, { vendor_emails: emails }); toast.success(`Quote requests sent to ${data.sent} supplier(s)`); }, "Suppliers invited", "Failed to send");

  const approvedTotal = useMemo(() => quotes.filter(q => q.approved).reduce((s, q) => s + quoteTotals(q).total, 0), [quotes]);
  const amounts = quotes.map(q => q.amount || 0);

  return (
    <div className="border border-border rounded-md p-4" data-testid={`${testPrefix}-quotes-panel`}>
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Quotes ({quotes.length})</h3>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            {approvedTotal > 0 ? <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300 font-semibold" data-testid={`${testPrefix}-quotes-approved-total`}><SealCheck size={11} weight="fill" /> {fmtMoney(approvedTotal)} approved (to client, ex VAT)</span> : "Select the quote you want to go with, then Approve once the client has said yes."}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {quotes.length >= 2 && <Button size="sm" variant="outline" onClick={() => setShowCompare(true)} data-testid={`${testPrefix}-compare-quotes-btn`}><Crown size={14} className="mr-1 text-amber-500" /> Compare</Button>}
          {quotes.length > 0 && <ClientApprovalLinkButton basePath={base} hasSelected={!!job.selected_quote_id} clientEmail={job.client_email} testPrefix={testPrefix} onGenerated={refresh} />}
          <Button size="sm" variant="outline" onClick={() => setShowInvite(true)} data-testid={`${testPrefix}-request-quotes-btn`}><PaperPlaneTilt size={14} className="mr-1" /> Invite Supplier</Button>
          <AddQuoteMenu builderLink={builderLink} onQuick={() => setShowAdd(true)} testPrefix={testPrefix} />
        </div>
      </div>
      <OriginStrip jr={jr} onChanged={refresh} />
      {quotes.length === 0 ? (
        <div className="text-center py-8 border border-dashed border-border rounded-md" data-testid={`${testPrefix}-quotes-empty`}>
          <CurrencyGbp size={28} className="mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground mb-2">No quotes yet</p>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <Button size="sm" onClick={() => navigate(`${builderLink}&source_type=supplier`)} data-testid={`${testPrefix}-empty-add-quote`}><Truck size={13} className="mr-1" /> Add Supplier Quote</Button>
            <Button size="sm" onClick={() => navigate(builderLink)} data-testid={`${testPrefix}-empty-add-quote-in-house`}><House size={13} className="mr-1" /> Create In-House Quote</Button>
            <Button size="sm" variant="outline" onClick={() => setShowAdd(true)} data-testid={`${testPrefix}-empty-quick-quote`}><Plus size={13} className="mr-1" /> Quick add</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {quotes.map(q => (
            <SupplierQuoteCard key={q.id} q={q} isSelected={q.id === job.selected_quote_id} builder={builders[q.in_house_quote_id]} testPrefix={testPrefix}
              onSelect={selectQuote} onApprove={approveQuote} onRaiseWo={raiseWo} onEdit={setEditing} onDelete={deleteQuote} onReverseCalc={setReverseCalc} onChanged={refresh} />
          ))}
          {quotes.length >= 2 && (
            <div className="border border-border rounded-md p-3 bg-muted/30" data-testid={`${testPrefix}-quote-comparison`}>
              <h4 className="text-xs font-semibold mb-2">Quote Comparison</h4>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div><span className="text-muted-foreground">Lowest:</span> <span className="font-bold">£{Math.min(...amounts).toLocaleString()}</span></div>
                <div><span className="text-muted-foreground">Highest:</span> <span className="font-bold">£{Math.max(...amounts).toLocaleString()}</span></div>
                <div><span className="text-muted-foreground">Average:</span> <span className="font-bold">£{Math.round(amounts.reduce((s, a) => s + a, 0) / amounts.length).toLocaleString()}</span></div>
              </div>
            </div>
          )}
        </div>
      )}

      {showAdd && <QuickQuoteDialog open onOpenChange={setShowAdd} initial={EMPTY_QUICK_QUOTE} onSubmit={addQuote} suppliers={suppliers} markupTemplates={markupTemplates} folder="job-quotes" testPrefix="quote" />}
      {editing && <QuickQuoteDialog open mode="edit" onOpenChange={(o) => { if (!o) setEditing(null); }} onSubmit={saveQuote} markupTemplates={markupTemplates} folder="job-quotes" testPrefix="quote"
        initial={{ vendor_name: editing.vendor_name, vendor_email: editing.vendor_email, vendor_phone: editing.vendor_phone, vendor_company: editing.vendor_company, amount: editing.amount, description: editing.description, validity_date: editing.validity_date, attachments: editing.attachments || [], markup_a_pct: editing.markup_a_pct || 0, markup_b_pct: editing.markup_b_pct || 0, markup_a_label: editing.markup_a_label || "Our Mark Up", markup_b_label: editing.markup_b_label || "Client Mark Up" }} />}
      <InviteSuppliersDialog open={showInvite} onOpenChange={setShowInvite} onSend={invite} briefName="Job Brief PDF" />
      <QuoteComparisonDialog jrId={job.id} basePath={base} open={showCompare} onClose={() => setShowCompare(false)} onSelected={refresh} onSelectQuote={selectQuote} />
      <ReverseCalcDialog open={!!reverseCalc} onOpenChange={(o) => { if (!o) setReverseCalc(null); }}
        initialMarkupA={reverseCalc?.markup_a_pct || 0} initialMarkupB={reverseCalc?.markup_b_pct || 0}
        initialMarkupALabel={reverseCalc?.markup_a_label || "Our Mark Up"} initialMarkupBLabel={reverseCalc?.markup_b_label || "Client Mark Up"}
        initialVatEnabled={false} initialVatRate={20} initialTotal={reverseCalc ? (quoteTotals(reverseCalc).total || 1000) : 1000}
        contextLabel={reverseCalc ? `${reverseCalc.vendor_name || ""} · cost £${(reverseCalc.amount || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}` : ""} />
    </div>
  );
}
